import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import {
  PhysicsWorld,
  COLLISION_GROUP_STATIC,
  COLLISION_GROUP_DYNAMIC_PROP,
  COLLISION_GROUP_CHASSIS,
  COLLISION_GROUP_GROUND,
  COLLISION_GROUP_AI_VEHICLE,
} from '../core/PhysicsWorld';
import { TrafficSigns } from './TrafficSigns';

export class Roundabout {
  public group: THREE.Group;
  public center = new THREE.Vector3(0, 0, 0);
  public outerRadius = 18.0;
  public innerRadius = 10.5;

  constructor(private physicsWorld: PhysicsWorld) {
    this.group = new THREE.Group();
    this.buildRoundaboutGeometry();
    this.buildSignage();
    this.setupPhysicsObstacles();
  }

  private buildRoundaboutGeometry() {
    // 1. Center Island (Grass mound with curbs)
    const islandGroup = new THREE.Group();

    // Grass Dome (Gentle, low German roundabout island)
    const grassMat = new THREE.MeshStandardMaterial({ color: 0x166534, roughness: 0.9 });
    const grassGeom = new THREE.CylinderGeometry(this.innerRadius - 1.2, this.innerRadius - 0.5, 0.20, 36);
    const grassMesh = new THREE.Mesh(grassGeom, grassMat);
    grassMesh.position.y = 0.10;
    islandGroup.add(grassMesh);

    // German Decorative Center Shrub / Tree / Flowerbed
    const treeTrunk = new THREE.Mesh(
      new THREE.CylinderGeometry(0.18, 0.24, 2.6, 12),
      new THREE.MeshStandardMaterial({ color: 0x451a03 })
    );
    treeTrunk.position.y = 1.3;
    const treeFoliage = new THREE.Mesh(
      new THREE.DodecahedronGeometry(2.0),
      new THREE.MeshStandardMaterial({ color: 0x15803d, roughness: 0.8 })
    );
    treeFoliage.position.y = 3.3;
    islandGroup.add(treeTrunk, treeFoliage);

    // Inner Overrun Pflasterstreifen (Cobblestone apron for large trucks/buses)
    const apronMat = new THREE.MeshStandardMaterial({ color: 0x78716c, roughness: 0.85 });
    const apronGeom = new THREE.RingGeometry(this.innerRadius - 1.2, this.innerRadius, 36);
    const apronMesh = new THREE.Mesh(apronGeom, apronMat);
    apronMesh.rotation.x = -Math.PI / 2;
    apronMesh.position.y = 0.04;
    islandGroup.add(apronMesh);

    this.group.add(islandGroup);

    // 2. Circulating Asphalt Road (Width ~7.5m)
    const roadMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.75 });
    const roadGeom = new THREE.RingGeometry(this.innerRadius, this.outerRadius, 48);
    const roadMesh = new THREE.Mesh(roadGeom, roadMat);
    roadMesh.rotation.x = -Math.PI / 2;
    roadMesh.position.y = 0.01;
    roadMesh.receiveShadow = true;
    this.group.add(roadMesh);

    // 3. Circular Markings (White boundary lines)
    const lineMat = new THREE.MeshBasicMaterial({ color: 0xffffff });

    // Inner line: unbroken circular ring around cobblestone apron
    const innerLine = new THREE.Mesh(new THREE.RingGeometry(this.innerRadius, this.innerRadius + 0.15, 48), lineMat);
    innerLine.rotation.x = -Math.PI / 2;
    innerLine.position.y = 0.015;
    this.group.add(innerLine);

    // Outer line: 4 quadrant arcs leaving the 4 road arm entrances & exits completely clear (no solid line blocking traffic!)
    const armHalfAngle = 0.24; // ~13.7 degrees each side, 8m road opening at r=18m
    for (let q = 0; q < 4; q++) {
      const startAngle = q * (Math.PI / 2) + armHalfAngle;
      const arcLen = (Math.PI / 2) - armHalfAngle * 2;
      const quadRingGeom = new THREE.RingGeometry(this.outerRadius - 0.15, this.outerRadius, 16, 1, startAngle, arcLen);
      const quadLine = new THREE.Mesh(quadRingGeom, lineMat);
      quadLine.rotation.x = -Math.PI / 2;
      quadLine.position.y = 0.015;
      this.group.add(quadLine);
    }

    // 4. Red Cycle Path (Radweg) on outer perimeter
    const bikeMat = new THREE.MeshStandardMaterial({ color: 0x991b1b, roughness: 0.8 });
    const bikeGeom = new THREE.RingGeometry(this.outerRadius + 0.2, this.outerRadius + 2.2, 48);
    const bikeMesh = new THREE.Mesh(bikeGeom, bikeMat);
    bikeMesh.rotation.x = -Math.PI / 2;
    bikeMesh.position.y = 0.012;
    this.group.add(bikeMesh);

    // Bicycle icons stenciled on cycle path
    this.buildCyclePathStencils();

    // 5. 4 Entry & Exit Arms (North, South, East, West) with StVO markings & Zebrastreifen
    const angles = [0, Math.PI / 2, Math.PI, (Math.PI * 3) / 2];
    angles.forEach((angle) => {
      this.buildArmMarkings(angle);
    });
  }

  private buildCyclePathStencils() {
    if (typeof document === 'undefined') return;
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d')!;

    ctx.clearRect(0, 0, 128, 128);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 10;
    // Wheels
    ctx.beginPath();
    ctx.arc(36, 85, 20, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(92, 85, 20, 0, Math.PI * 2);
    ctx.stroke();
    // Frame
    ctx.beginPath();
    ctx.moveTo(36, 85);
    ctx.lineTo(60, 85);
    ctx.lineTo(76, 50);
    ctx.lineTo(52, 50);
    ctx.closePath();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(92, 85);
    ctx.lineTo(80, 36);
    ctx.lineTo(88, 34);
    ctx.stroke();

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 8;
    const bikeMat = new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity: 0.9 });

    // Place bicycle icons between arms on cycle path
    const stencilAngles = [Math.PI / 4, (3 * Math.PI) / 4, (5 * Math.PI) / 4, (7 * Math.PI) / 4];
    for (const a of stencilAngles) {
      const bikeMesh = new THREE.Mesh(new THREE.PlaneGeometry(1.2, 1.2), bikeMat);
      bikeMesh.rotation.x = -Math.PI / 2;
      bikeMesh.rotation.z = a + Math.PI / 2;
      bikeMesh.position.set(
        (this.outerRadius + 1.2) * Math.sin(a),
        0.016,
        (this.outerRadius + 1.2) * Math.cos(a)
      );
      this.group.add(bikeMesh);
    }
  }

  private buildArmMarkings(angle: number) {
    const armGroup = new THREE.Group();
    armGroup.rotation.y = angle;

    const lineMat = new THREE.MeshBasicMaterial({ color: 0xffffff });

    // 1. Radwegefurt (Zeichen 340 - cycle path crossing dashed squares across entry & exit)
    // 30cm x 30cm white blocks along both edges of cycle crossing at r=18.3 and r=20.1
    const blockGeom = new THREE.PlaneGeometry(0.35, 0.35);
    for (let x = -3.8; x <= 3.8; x += 0.85) {
      // Inner edge of cycle track
      const bInner = new THREE.Mesh(blockGeom, lineMat);
      bInner.rotation.x = -Math.PI / 2;
      bInner.position.set(x, 0.016, this.outerRadius + 0.25);

      // Outer edge of cycle track
      const bOuter = new THREE.Mesh(blockGeom, lineMat);
      bOuter.rotation.x = -Math.PI / 2;
      bOuter.position.set(x, 0.016, this.outerRadius + 2.15);

      armGroup.add(bInner, bOuter);
    }

    // 2. Haifischzähne (Zeichen 342 - Wartelinie) strictly on ENTRY lane (right half of approaching road)
    // In local coords, approaching car travels towards -Z:
    // Right side of road is x in [0.2, 3.8]. Tips of triangles point towards +Z (approaching driver)
    const teethCanvas = document.createElement('canvas');
    teethCanvas.width = 256;
    teethCanvas.height = 64;
    const ctx = teethCanvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, 256, 64);
      ctx.fillStyle = '#ffffff';
      const numTeeth = 5;
      const tW = 256 / numTeeth;
      for (let i = 0; i < numTeeth; i++) {
        ctx.beginPath();
        // Base is at y=6 (towards roundabout / -Z), tip at y=58 (towards driver / +Z)
        ctx.moveTo(i * tW + 4, 6);
        ctx.lineTo((i + 0.5) * tW, 58);
        ctx.lineTo((i + 1) * tW - 4, 6);
        ctx.closePath();
        ctx.fill();
      }
      const teethTex = new THREE.CanvasTexture(teethCanvas);
      const teethMesh = new THREE.Mesh(
        new THREE.PlaneGeometry(3.6, 0.75),
        new THREE.MeshBasicMaterial({ map: teethTex, transparent: true })
      );
      teethMesh.rotation.x = -Math.PI / 2;
      teethMesh.position.set(2.0, 0.018, this.outerRadius + 2.6);
      armGroup.add(teethMesh);
    }

    // 3. Zebrastreifen (Zeichen 293) at pedestrian crossing ~8.5m before circle
    // On North arm (angle === 0), omit redundant zebra because main boulevard has authentic crossing at z = 42
    if (Math.abs(angle) > 0.01) {
      for (let i = -3; i <= 3; i++) {
        const stripe = new THREE.Mesh(new THREE.PlaneGeometry(0.5, 3.2), lineMat);
        stripe.rotation.x = -Math.PI / 2;
        stripe.position.set(i * 0.9, 0.018, this.outerRadius + 8.5);
        armGroup.add(stripe);
      }
    }

    this.group.add(armGroup);
  }

  private buildSignage() {
    const armAngles = [0, Math.PI / 2, Math.PI, (Math.PI * 3) / 2];
    armAngles.forEach((angle) => {
      // Authentic German StVO sign combination: Zeichen 205 (top) + Zeichen 215 (bottom with 3 rotating arrows)
      // Mounted on single post 12m before entry on the right curb
      const signPos = new THREE.Vector3(4.5, 0, this.outerRadius + 12).applyAxisAngle(new THREE.Vector3(0, 1, 0), angle);
      const signGroup = TrafficSigns.createKreisverkehrSignPost(signPos, angle);
      this.group.add(signGroup);

      // Physical signpost collider
      const postBody = new CANNON.Body({
        type: CANNON.Body.STATIC,
        material: this.physicsWorld.dynamicPropMaterial,
        collisionFilterGroup: COLLISION_GROUP_DYNAMIC_PROP,
        collisionFilterMask:
          COLLISION_GROUP_GROUND |
          COLLISION_GROUP_CHASSIS |
          COLLISION_GROUP_AI_VEHICLE |
          COLLISION_GROUP_STATIC,
      });
      postBody.addShape(new CANNON.Cylinder(0.08, 0.08, 3.0, 8), new CANNON.Vec3(0, 1.5, 0));
      postBody.position.set(signPos.x, 0, signPos.z);
      this.physicsWorld.world.addBody(postBody);
    });
  }

  private setupPhysicsObstacles() {
    // Physical Center Island Obstacle Body
    const islandBody = new CANNON.Body({
      type: CANNON.Body.STATIC,
      material: this.physicsWorld.obstacleMaterial,
      collisionFilterGroup: COLLISION_GROUP_STATIC,
      collisionFilterMask: COLLISION_GROUP_CHASSIS | COLLISION_GROUP_AI_VEHICLE,
    });
    islandBody.addShape(new CANNON.Cylinder(this.innerRadius, this.innerRadius, 0.8, 16), new CANNON.Vec3(0, 0.4, 0));
    this.physicsWorld.world.addBody(islandBody);
  }

  public isInsideRoundabout(pos: THREE.Vector3): boolean {
    const dist = Math.hypot(pos.x - this.center.x, pos.z - this.center.z);
    return dist >= this.innerRadius && dist <= this.outerRadius + 2.0;
  }
}
